// =====================================================
// PUBLIC APPOINTMENT ACTION PAGE
// Pagina publica para que pacientes confirmen/cancelen
// turnos desde links de WhatsApp (sin autenticacion)
// =====================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Briefcase,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface AppointmentData {
  id: number;
  date: string;
  time: string;
  duration: number;
  status: string;
  clientName: string;
  serviceName: string;
  notes: string;
  cancellationReason: string;
  professional: {
    name: string;
    address: string;
    specialty: string;
    image: string | null;
  };
  isPast: boolean;
  canConfirm: boolean;
  canCancel: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  pending: {
    label: 'Pendiente de confirmacion',
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200',
    icon: <AlertCircle className="w-5 h-5 text-amber-500" />
  },
  confirmed: {
    label: 'Confirmado',
    color: 'text-green-700',
    bg: 'bg-green-50 border-green-200',
    icon: <CheckCircle2 className="w-5 h-5 text-green-500" />
  },
  cancelled: {
    label: 'Cancelado',
    color: 'text-red-700',
    bg: 'bg-red-50 border-red-200',
    icon: <XCircle className="w-5 h-5 text-red-500" />
  },
  completed: {
    label: 'Completado',
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200',
    icon: <CheckCircle2 className="w-5 h-5 text-blue-500" />
  },
  'no-show': {
    label: 'No asistio',
    color: 'text-gray-700',
    bg: 'bg-gray-50 border-gray-200',
    icon: <XCircle className="w-5 h-5 text-gray-500" />
  }
};

const PublicAppointmentAction: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [appointment, setAppointment] = useState<AppointmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<'confirm' | 'cancel' | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [actionSuccess, setActionSuccess] = useState<'confirmed' | 'cancelled' | null>(null);

  const fetchAppointment = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_URL}/api/public/appointments/${token}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || 'No se pudo obtener la informacion del turno');
        return;
      }

      setAppointment(data.appointment);
    } catch (err) {
      setError('Error de conexion. Intenta nuevamente.');
      console.error('Error fetching appointment:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchAppointment();
    }
  }, [token, fetchAppointment]);

  const handleConfirm = async () => {
    try {
      setActionLoading('confirm');
      const response = await fetch(`${API_URL}/api/public/appointments/${token}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || 'Error al confirmar el turno');
        return;
      }

      setActionSuccess('confirmed');
      setAppointment(prev => prev ? {
        ...prev,
        status: 'confirmed',
        canConfirm: false,
        canCancel: true
      } : null);
    } catch (err) {
      setError('Error de conexion. Intenta nuevamente.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    try {
      setActionLoading('cancel');
      const response = await fetch(`${API_URL}/api/public/appointments/${token}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cancellationReason: cancelReason || undefined })
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || 'Error al cancelar el turno');
        setShowCancelModal(false);
        return;
      }

      setActionSuccess('cancelled');
      setShowCancelModal(false);
      setAppointment(prev => prev ? {
        ...prev,
        status: 'cancelled',
        canConfirm: false,
        canCancel: false,
        cancellationReason: cancelReason || 'Cancelado por el paciente'
      } : null);
    } catch (err) {
      setError('Error de conexion. Intenta nuevamente.');
      setShowCancelModal(false);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      // Extraer solo YYYY-MM-DD si viene como ISO completo (2026-02-24T00:00:00.000Z)
      const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const date = new Date(dateOnly + 'T12:00:00');
      return date.toLocaleDateString('es-AR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr: string) => {
    return timeStr ? timeStr.substring(0, 5) + ' hs' : '';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Cargando informacion del turno...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !appointment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Turno no encontrado</h2>
          <p className="text-gray-500 text-sm mb-6">{error}</p>
          <p className="text-gray-400 text-xs">
            Si crees que es un error, contacta al profesional directamente.
          </p>
        </div>
      </div>
    );
  }

  if (!appointment) return null;

  const statusConfig = STATUS_CONFIG[appointment.status] || STATUS_CONFIG.pending;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-indigo-600">ConsultAR</h1>
          <p className="text-sm text-gray-400 mt-1">Gestion de turnos</p>
        </div>

        {/* Card principal */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header con nombre del paciente */}
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-5 text-white">
            <p className="text-indigo-100 text-xs font-medium uppercase tracking-wider mb-1">Tu turno</p>
            <h2 className="text-lg font-bold">{appointment.clientName}</h2>
          </div>

          {/* Status badge */}
          <div className={`mx-6 -mt-3 px-4 py-2.5 rounded-xl border ${statusConfig.bg} flex items-center gap-2`}>
            {statusConfig.icon}
            <span className={`text-sm font-semibold ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
          </div>

          {/* Detalles del turno */}
          <div className="px-6 py-5 space-y-4">
            {/* Profesional */}
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <User className="w-4.5 h-4.5 text-indigo-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">Profesional</p>
                <p className="text-sm font-semibold text-gray-900">{appointment.professional.name}</p>
                {appointment.professional.specialty && (
                  <p className="text-xs text-gray-500">{appointment.professional.specialty}</p>
                )}
              </div>
            </div>

            {/* Servicio */}
            {appointment.serviceName && (
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Briefcase className="w-4.5 h-4.5 text-purple-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium">Servicio</p>
                  <p className="text-sm font-semibold text-gray-900">{appointment.serviceName}</p>
                  {appointment.duration && (
                    <p className="text-xs text-gray-500">{appointment.duration} minutos</p>
                  )}
                </div>
              </div>
            )}

            {/* Fecha y hora */}
            <div className="flex gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Calendar className="w-4.5 h-4.5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium">Fecha</p>
                  <p className="text-sm font-semibold text-gray-900 capitalize">{formatDate(appointment.date)}</p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-sky-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Clock className="w-4.5 h-4.5 text-sky-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">Hora</p>
                <p className="text-sm font-semibold text-gray-900">{formatTime(appointment.time)}</p>
              </div>
            </div>

            {/* Direccion */}
            {appointment.professional.address && (
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="w-4.5 h-4.5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium">Direccion</p>
                  <p className="text-sm font-semibold text-gray-900">{appointment.professional.address}</p>
                </div>
              </div>
            )}

            {/* Notas */}
            {appointment.notes && (
              <div className="bg-gray-50 rounded-xl p-3 mt-2">
                <p className="text-xs text-gray-400 font-medium mb-1">Notas</p>
                <p className="text-sm text-gray-700">{appointment.notes}</p>
              </div>
            )}

            {/* Motivo de cancelacion */}
            {appointment.status === 'cancelled' && appointment.cancellationReason && (
              <div className="bg-red-50 rounded-xl p-3 mt-2">
                <p className="text-xs text-red-400 font-medium mb-1">Motivo de cancelacion</p>
                <p className="text-sm text-red-700">{appointment.cancellationReason}</p>
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mx-6 mb-4 bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Success message */}
          {actionSuccess && (
            <div className={`mx-6 mb-4 rounded-xl p-4 text-center ${
              actionSuccess === 'confirmed'
                ? 'bg-green-50 border border-green-200'
                : 'bg-orange-50 border border-orange-200'
            }`}>
              {actionSuccess === 'confirmed' ? (
                <>
                  <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-green-700">Turno confirmado exitosamente</p>
                  <p className="text-xs text-green-600 mt-1">El profesional fue notificado</p>
                </>
              ) : (
                <>
                  <XCircle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-orange-700">Turno cancelado</p>
                  <p className="text-xs text-orange-600 mt-1">El profesional fue notificado de la cancelacion</p>
                </>
              )}
            </div>
          )}

          {/* Turno pasado */}
          {appointment.isPast && (
            <div className="mx-6 mb-4 bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
              <p className="text-sm text-gray-500">Este turno ya paso. No se pueden realizar acciones.</p>
            </div>
          )}

          {/* Action buttons */}
          {!actionSuccess && !appointment.isPast && (appointment.canConfirm || appointment.canCancel) && (
            <div className="px-6 pb-6 space-y-3">
              {/* Separador */}
              <div className="border-t border-gray-100 pt-4" />

              {appointment.canConfirm && (
                <button
                  onClick={handleConfirm}
                  disabled={actionLoading !== null}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                >
                  {actionLoading === 'confirm' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5" />
                  )}
                  {actionLoading === 'confirm' ? 'Confirmando...' : 'Confirmar turno'}
                </button>
              )}

              {appointment.canCancel && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  disabled={actionLoading !== null}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-white hover:bg-red-50 text-red-600 border border-red-200 rounded-xl transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircle className="w-4.5 h-4.5" />
                  Cancelar turno
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          ConsultAR &middot; Gestion de turnos online
        </p>
      </div>

      {/* Modal de cancelacion */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl">
            <div className="p-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                Cancelar turno
              </h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                Estas seguro que queres cancelar tu turno? Esta accion no se puede deshacer.
              </p>

              {/* Textarea para motivo (opcional) */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Motivo (opcional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Contanos por que cancelas..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
                disabled={actionLoading !== null}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-medium text-sm disabled:opacity-50"
              >
                Volver
              </button>
              <button
                onClick={handleCancel}
                disabled={actionLoading !== null}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors font-medium text-sm disabled:opacity-50"
              >
                {actionLoading === 'cancel' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                {actionLoading === 'cancel' ? 'Cancelando...' : 'Si, cancelar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicAppointmentAction;
